'use client';

import { useEffect } from 'react';
import { DashboardLinks } from '@/components/pages/dashboard/constants';
import useCustomRouter from '@/hooks/useCustomRouter';
import useAccount from '@/lib/hooks/useAccount';
import useShoppingListAccess from '@/lib/hooks/useShoppingListAccess';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';

// EWM-298 (OQ-2, Luke 2026-06-14) — client-side guard for the Master-only
// Purchase Lists routes. A logged-in View-Only user (account present, no
// `UpdateMyCarts` permission) is redirected to the account dashboard. Guests are
// already bounced to the sign-in flow by the route auth-gate (protected-segments
// + proxy), so we don't redirect them here.
//
// Mirrors the checkout route guard (components/pages/checkout-v2/index.tsx),
// which waits for a ready signal before deciding so a still-loading Master is
// never prematurely bounced. Here the ready signal is both hooks reporting
// `!isLoading` — `useShoppingListAccess` reports `isLoading` (and empty permissions)
// while in flight, which would otherwise read as View-Only.
export type RequireMasterStatus = 'pending' | 'allowed' | 'redirecting';

const useRequireMaster = (): RequireMasterStatus => {
  const router = useCustomRouter();
  const { loggedIn, isLoading: accountLoading } = useAccount();
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();
  // EWM-653 — reuse the centralized capability so "who is a Master" is defined
  // in exactly one place. canUsePurchaseLists === (account && UpdateMyCarts).
  const { canUsePurchaseLists, isLoading: rolesLoading } = useShoppingListAccess(selectedBusinessUnit?.key);

  const ready = !accountLoading && !rolesLoading;
  const blocked = ready && loggedIn && !canUsePurchaseLists;

  useEffect(() => {
    if (blocked) router.replace(DashboardLinks.dashboard);
  }, [blocked, router]);

  if (!ready) return 'pending';
  if (blocked) return 'redirecting';
  return 'allowed';
};

export default useRequireMaster;
